<template>
  <div>
    <icon-view
      gradient
      icon="fa fa-book font-14"
      icon-background="#FFF"
      icon-color="#FF7058"
      title="Bitácora"
    />
    <div
      v-if="items.length === 0 && !showAdd"
      class="bg-secondary-13 rounded py-3 mt-3"
    >
      <div class="row d-flex justify-content-center">
        <action-button
          id="btnAdd"
          class="border w-40 btn bg-white"
          i="fas fa-file-plus color-primary-1"
          text="Presione aquí para añadir bitácora"
          @click="showAdd = !showAdd"
        />
      </div>
    </div>
    <div v-if="showAdd" class="bg-secondary-13 rounded py-3 px-4 mt-3">
      <validation-observer ref="formRef" novalidate tag="form">
        <validation-provider v-slot="{ errors }" rules="required">
          <div class="color-black font-14 my-1">Bitácora de Intervención:</div>
          <custom-text-area
            id="txtComment"
            v-model="model.comment"
            :count-classes="['d-block', 'small-counter', 'text-right']"
            custom-error-msg="Favor escriba un comentario de la intervención realizada."
            :error="errors[0]"
            :maxlength="3000"
            placeholder="Escribe un comentario"
            show-char-count
          />
        </validation-provider>
      </validation-observer>
      <div class="d-flex justify-content-end align-items-center mt-4">
        <action-button
          id="btnCancel"
          i="fas fa-times-circle"
          text="Cancelar"
          variant="outline-danger mr-3 px-3"
          @click="onCancelHandler"
        />
        <action-button
          id="btnSave"
          i="fas fa-save"
          text="Guardar"
          variant="success px-3"
          @click="onSaveHandler"
        />
      </div>
    </div>
    <div>
      <div
        v-for="(item, index) in items"
        :key="`item-${index}`"
        class="d-flex flex-column flex-md-row mb-3 bg-secondary-13 rounded py-3 px-4 mt-3"
      >
        <div class="col pr-5 border-right">
          <div class="d-flex align-items-center">
            <em
              class="fas fa-circle mr-2"
              :class="`color-${statusColor(item.billingStatusId)}`"
            />
            <span class="color-black font-14 font-weight-bold">
              {{ item.billingStatusName }}
            </span>
          </div>
          <div class="mt-2">
            <div class="color-secondary-6 font-14">Fecha:</div>
            <div class="font-16 color-black">
              {{ formatCreatedDate(item.createdDate) }}
            </div>
          </div>
          <div class="mt-2">
            <div class="color-secondary-6 font-14">Añadido por:</div>
            <div class="font-16 color-black">{{ item.createdByUserName }}</div>
          </div>
        </div>
        <div class="col-10 mt-2 mt-md-0 pl-0 pl-md-4">
          <div>
            <div class="color-secondary-6 font-14 mb-2">Comentario:</div>
            <div class="bg-white px-4 py-2 rounded border-color--gray-5">
              <p>{{ itemComment(item) }}</p>
              <div
                v-if="showReadMore(item.comment)"
                class="text-right cursor-pointer"
                @click="onShowMoreTextHandler(item)"
              >
                <span class="color-green mr-2">
                  {{ readText(item.showFullText) }}
                </span>
                <em :class="`color-green ${readIcon(item.showFullText)}`" />
              </div>
            </div>
          </div>
        </div>
      </div>
      <div v-if="showAddAction" class="text-right">
        <action-button
          id="btnAddEntry"
          i="fas fa-file-plus"
          text="Añadir"
          variant="success pl-4 pr-4"
          @click="showAdd = !showAdd"
        />
      </div>
      <div
        v-if="showPagination"
        class="d-flex justify-content-center align-items-center mt-3"
      >
        <action-button
          class="btn-see-more"
          i="fas fa-chevron-down"
          text="Ver más"
          variant="outline-success rounded-pill px-5"
          @click="onLoadMoreHandler"
        />
      </div>
    </div>
  </div>
</template>

<script>
//libs
import ActionButton from 'ActionButton';
import CustomTextArea from 'CustomTextArea';

//components
import IconView from './IconView';

//utils
import alertMixin from '@quisit/mipe-utils/mixins/alert.mixin.js';
import billingStatus from '@/utils/constants/billingStatus';
import { Colors } from '@/components/common';

//services
import {
  createJournalTracking as _createJournalTracking,
  getJournalTracking as _getJournalTracking,
} from '@/modules/billingHistory/api';
import moment from 'moment/moment';

//constants
const DEFAULT_PAGE_SIZE = 3;
const MAX_CHARACTER_LEN = 500;

export default {
  name: 'JournalInfo',
  components: {
    ActionButton,
    CustomTextArea,
    IconView,
  },
  mixins: [alertMixin],
  props: {
    billId: {
      type: Number,
      required: true,
    },
  },
  data: () => ({
    model: {
      comment: null,
    },
    items: [],
    pageSize: DEFAULT_PAGE_SIZE,
    pagination: {
      pageSize: DEFAULT_PAGE_SIZE,
      currentPage: 1,
      totalItemCount: 0,
      pageCount: 0,
      count: 0,
    },
    showAdd: false,
  }),
  computed: {
    statusColor() {
      return (billingStatusId) => {
        return {
          [billingStatus.Aprobado]: 'green-light',
          [billingStatus.Pagado]: 'green-light',
          [billingStatus.EnviadoFinanzas]: Colors.CHARDONNAY,
          [billingStatus.EnviadoFinanzasParcial]: Colors.CHARDONNAY,
          [billingStatus.Sometida]: Colors.YELLOW,
          [billingStatus.PendienteIntervencion]: Colors.YELLOW,
          [billingStatus.PendienteInformacion]: Colors.YELLOW,
          [billingStatus.PendienteAprobacion]: Colors.YELLOW,
          [billingStatus.PendientePreAprobacion]: Colors.YELLOW,
          [billingStatus.PendienteRevision]: Colors.YELLOW,
          [billingStatus.PendienteIntervencionRevision]: Colors.YELLOW,
          [billingStatus.EnProcesoIntervencion]: Colors.YELLOW,
          [billingStatus.EnProcesoDeAjuste]: Colors.YELLOW,
          [billingStatus.EnProcesoIntervencionObjetada]: Colors.VIOLET,
          [billingStatus.PendienteIntervencionObjetada]: Colors.VIOLET,
          [billingStatus.Preliminar]: Colors.BROWN,
          [billingStatus.CanceladoPorProveedor]: Colors.BROWN,
        }[billingStatusId || ''];
      };
    },
    formatCreatedDate() {
      return (createdDate) =>
        moment(createdDate)
          .locale('es')
          .format('DD/MMM/YYYY hh:mma');
    },
    showAddAction() {
      return this.items.length && !this.showAdd;
    },
    showPagination() {
      return this.pagination.data.length > 0 && this.pagination.pageCount > 1;
    },
    itemComment() {
      return (item) =>
        item.showFullText
          ? item.comment
          : item.comment.length > 500
          ? item.comment.slice(0, MAX_CHARACTER_LEN) + '...'
          : item.comment;
    },
    showReadMore() {
      return (comment) => comment.length > MAX_CHARACTER_LEN;
    },
    readText() {
      return (showFullText) => (showFullText ? 'Leer Menos' : 'Leer Más');
    },
    readIcon() {
      return (showFullText) =>
        !showFullText ? 'fa fa-angle-down' : 'fa fa-angle-up';
    },
  },
  async mounted() {
    await this.getJournalTracking();
  },
  methods: {
    async getJournalTracking() {
      const response = (
        await _getJournalTracking(this.billId, 1, this.pageSize)
      ).data;
      this.items = response.data.map((x) => ({ ...x, showFullText: false }));
      this.pagination = {
        ...this.response,
        data: response.data,
        totalItemCount: response.totalItemCount,
        pageCount: response.pageCount,
        currentPage: response.currentPage,
        count: response.count,
      };
    },
    async createJournalTracking() {
      const payload = {
        billId: this.billId,
        comment: this.model.comment,
      };
      const { data } = await _createJournalTracking(payload).catch((error) => {
        this.ShowToast(
          'Alerta',
          `${error.response.data.message}.`,
          'error',
          null
        );
        return { data: false };
      });
      if (data) {
        this.pageSize = DEFAULT_PAGE_SIZE;
        await this.getJournalTracking();
        this.onCancelHandler();
      }
    },
    onCancelHandler() {
      this.model.comment = null;
      this.showAdd = false;
    },
    async onSaveHandler() {
      const isFormValid = await this.$refs.formRef.validate();
      if (!isFormValid) {
        this.ShowToast(
          'Alerta',
          'Favor completar la información solicitada.',
          'error'
        );
        return;
      }
      await this.createJournalTracking();
    },
    onShowMoreTextHandler(item) {
      item.showFullText = !item.showFullText;
    },
    async onLoadMoreHandler() {
      this.pageSize = this.pageSize + DEFAULT_PAGE_SIZE;
      await this.getJournalTracking();
    },
  },
};
</script>
